import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { toCamelCase } from 'utils/strings'

import {
  DELIVERY,
  PARTICIPANTS_ALL_CATEGORY,
  PARTICIPANTS_EXCLUDE_FILTER_VALUES,
  PARTICIPANTS_NAMES_CATEGORY,
  PARTICIPANTS_PER_PAGE,
} from 'containers/AwarenessCenter/constants'

import {
  CHANGE_DELIVERY,
  CHANGE_PARTICIPANTS_SELECTION,
  CHANGE_PARTICIPANTS_TOTAL_SELECTION,
  GET_CAMPAIGN_ASYNC,
} from '../constants'
import { selectParticipants, selectSelectedDelivery } from '../selectors'

import {
  fetchQuickLaunchError,
  fetchQuickLaunchSuccess,
  getParticipantsError,
  getParticipantsInfoError,
  getParticipantsInfoSuccess,
  getParticipantsSuccess,
  searchParticipantsInfoError,
  searchParticipantsInfoSuccess,
  submitQuickLaunchError,
  submitQuickLaunchSuccess,
} from './actions'
import {
  FETCH_QUICK_LAUNCH_ASYNC,
  GET_PARTICIPANTS_ASYNC,
  GET_PARTICIPANTS_INFO_ASYNC,
  SEARCH_PARTICIPANTS_INFO_ASYNC,
  SUBMIT_QUICK_LAUNCH_ASYNC,
} from './constants'

const getParticipantsInfoEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_PARTICIPANTS_INFO_ASYNC.INITIATE, CHANGE_DELIVERY).mergeMap(() =>
    api.wizardCampaign
      .getParticipantsInfo(store.getState().partnerOptions.selectedCompany?.value, {
        deliveryType: selectSelectedDelivery(store.getState()),
        groups: selectParticipants(store.getState()).included,
        excludedGroups: selectParticipants(store.getState()).excluded,
      })
      .mergeMap((response) => Observable.of(getParticipantsInfoSuccess(response.response)))
      .catch((error) => Observable.of(epicsErrorHandler(getParticipantsInfoError, error, [400])))
      .takeUntil(action$.ofType(GET_PARTICIPANTS_INFO_ASYNC.CANCEL))
  )

const searchParticipantsInfoEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.INITIATE).mergeMap((action) =>
    api.wizardCampaign
      .getParticipantsInfo(store.getState().partnerOptions.selectedCompany?.value, {
        searchFilter: action.payload.value,
        deliveryType: selectSelectedDelivery(store.getState()),
        groups: selectParticipants(store.getState()).included,
        excludedGroups: selectParticipants(store.getState()).excluded,
      })
      .mergeMap((response) => Observable.of(searchParticipantsInfoSuccess(response.response)))
      .catch((error) => Observable.of(epicsErrorHandler(searchParticipantsInfoError, error, [400])))
      .takeUntil(action$.ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.CANCEL))
  )

const fetchQuickLaunchEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_QUICK_LAUNCH_ASYNC.INITIATE).mergeMap(() =>
    api.wizardCampaign
      .getQuickLaunch(store.getState().partnerOptions.selectedCompany?.value)
      .mergeMap((response) => Observable.of(fetchQuickLaunchSuccess(response)))
      .catch((error) => Observable.of(epicsErrorHandler(fetchQuickLaunchError, error, [400])))
      .takeUntil(action$.ofType(FETCH_QUICK_LAUNCH_ASYNC.CANCEL))
  )

const submitQuickLaunchEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SUBMIT_QUICK_LAUNCH_ASYNC.INITIATE).mergeMap((action) =>
    api.wizardCampaign
      .submitQuickLaunch(
        store.getState().partnerOptions.selectedCompany?.value,
        action.payload.data
      )
      .mergeMap(({ response }) => Observable.of(submitQuickLaunchSuccess(response)))
      .catch((error) => Observable.of(epicsErrorHandler(submitQuickLaunchError, error, [400])))
      .takeUntil(action$.ofType(SUBMIT_QUICK_LAUNCH_ASYNC.CANCEL))
  )

const getParticipantsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      GET_CAMPAIGN_ASYNC.SUCCESS,
      GET_PARTICIPANTS_INFO_ASYNC.SUCCESS,
      GET_PARTICIPANTS_ASYNC.INITIATE,
      CHANGE_PARTICIPANTS_SELECTION,
      CHANGE_PARTICIPANTS_TOTAL_SELECTION
    )
    .switchMap((action) => {
      let showIncluded
      let showExcluded
      if (
        action.type === GET_PARTICIPANTS_INFO_ASYNC.SUCCESS ||
        action.type === CHANGE_PARTICIPANTS_TOTAL_SELECTION
      ) {
        showIncluded = true
        showExcluded = false
      } else {
        // modal case
        const excludeFilter = selectParticipants(store.getState()).excludeFilter
        showIncluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.INCLUDED
        showExcluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.EXCLUDED
      }

      const participants = selectParticipants(store.getState())
      const include = {
        allCompany: false,
        show: showIncluded,
      }
      const exclude = {
        show: showExcluded,
      }

      Object.keys(participants.included).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_ALL_CATEGORY) {
          include.allCompany = true
        } else if (key === PARTICIPANTS_NAMES_CATEGORY) {
          include.emails = participants.included[key]
        } else {
          include[formattedKey] = participants.included[key]
        }
      })

      Object.keys(participants.excluded).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_NAMES_CATEGORY) {
          exclude.emails = participants.excluded[key]
        } else {
          exclude[formattedKey] = participants.excluded[key]
        }
      })

      if (include.emails?.length === 0) delete include.emails
      if (exclude.emails?.length === 0) delete exclude.emails

      return api.wizardCampaign
        .getParticipants(
          store.getState().partnerOptions.selectedCompany?.value,
          {
            search: selectParticipants(store.getState()).modalSearch,
            deliveryType: DELIVERY.EMAIL,
            include,
            exclude,
          },
          selectParticipants(store.getState()).page,
          PARTICIPANTS_PER_PAGE
        )
        .mergeMap((response) => Observable.of(getParticipantsSuccess(response.response)))
        .catch((error) => Observable.of(epicsErrorHandler(getParticipantsError, error, [400])))
        .takeUntil(action$.ofType(GET_PARTICIPANTS_ASYNC.CANCEL))
    })

export default combineEpics(
  getParticipantsInfoEpic,
  fetchQuickLaunchEpic,
  submitQuickLaunchEpic,
  getParticipantsEpic,
  searchParticipantsInfoEpic
)
